import { Check, ChevronDown, X } from 'lucide-react';
import { Popover as RP } from 'radix-ui';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cx } from '../lib/cx.js';
import { filterOptions } from '../lib/filter.js';
import type { ControlSize } from './Button.js';
import { useField } from './Field.js';
import { Spinner } from './Spinner.js';
import './field.css';
import './overlay.css';
import './combobox.css';

export interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
  keywords?: readonly string[];
  icon?: ReactNode;
  /** Small tag right after the label (e.g. the patient's office code). */
  badge?: ReactNode;
  disabled?: boolean;
}

export interface ComboboxProps {
  options: readonly ComboboxOption[];
  /** Selected value, or null for none. */
  value: string | null;
  onChange: (value: string | null, option: ComboboxOption | null) => void;
  /**
   * Called as the user types. When provided together with `filter={false}`,
   * the caller owns filtering (server search) and passes matching `options`.
   */
  onInputChange?: (text: string) => void;
  /** Built-in ranking (default) or false for caller-filtered options. */
  filter?: boolean;
  placeholder?: string;
  emptyText?: string;
  loading?: boolean;
  size?: ControlSize;
  disabled?: boolean;
  invalid?: boolean;
  clearable?: boolean;
  /** Max rows rendered; the rest are reachable by typing more. */
  limit?: number;
  /** Open the full list when the field is clicked or focused (short lists you browse). */
  openOnFocus?: boolean;
  'aria-label'?: string;
  className?: string;
}

/**
 * Typeahead select (WAI-ARIA 1.2 combobox with listbox popup). Focus stays
 * in the input; arrows move the active option, Enter selects, Escape closes
 * and then clears, Tab closes without changing the value.
 */
export function Combobox({
  options,
  value,
  onChange,
  onInputChange,
  filter = true,
  placeholder = 'Search',
  emptyText = 'No matches',
  loading = false,
  size = 'md',
  disabled,
  invalid,
  clearable = true,
  limit = 50,
  openOnFocus = false,
  className,
  ...aria
}: ComboboxProps) {
  const field = useField();
  const listId = useId();
  const optionId = (i: number) => `${listId}-opt-${i}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value) ?? null;
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(selected?.label ?? '');
  const [active, setActive] = useState(0);
  const [typing, setTyping] = useState(false);

  // When the selection changes from outside, show its label.
  useEffect(() => {
    if (!typing) setText(selected?.label ?? '');
  }, [selected, typing]);

  const shown = useMemo(() => {
    const list = filter && typing ? filterOptions(text, options) : [...options];
    return list.slice(0, limit);
  }, [filter, typing, text, options, limit]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  function commit(option: ComboboxOption | null) {
    onChange(option?.value ?? null, option);
    setText(option?.label ?? '');
    setTyping(false);
    setOpen(false);
  }

  function move(delta: number) {
    if (shown.length === 0) return;
    let next = active;
    for (let i = 0; i < shown.length; i += 1) {
      next = (next + delta + shown.length) % shown.length;
      if (!shown[next]?.disabled) break;
    }
    setActive(next);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setActive(
            Math.max(
              0,
              shown.findIndex((o) => o.value === value),
            ),
          );
        } else move(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) setOpen(true);
        else move(-1);
        break;
      case 'Home':
        if (open) {
          e.preventDefault();
          setActive(0);
        }
        break;
      case 'End':
        if (open) {
          e.preventDefault();
          setActive(Math.max(0, shown.length - 1));
        }
        break;
      case 'Enter': {
        if (!open) return;
        e.preventDefault();
        const option = shown[active];
        if (option && !option.disabled) commit(option);
        break;
      }
      case 'Escape':
        if (open) {
          e.preventDefault();
          e.stopPropagation();
          setOpen(false);
          setTyping(false);
          setText(selected?.label ?? '');
        } else if (clearable && value !== null) {
          e.preventDefault();
          commit(null);
        }
        break;
      case 'Tab':
        setOpen(false);
        setTyping(false);
        setText(selected?.label ?? '');
        break;
      default:
        break;
    }
  }

  const isInvalid = invalid ?? field?.invalid ?? false;
  const activeOption = open ? shown[active] : undefined;

  return (
    <RP.Root open={open && !disabled} onOpenChange={setOpen}>
      <RP.Anchor asChild>
        <div
          className={cx('vel-input', 'vel-combobox', className)}
          data-size={size}
          data-invalid={isInvalid ? '' : undefined}
          data-disabled={disabled ? '' : undefined}
          onPointerDown={(e) => {
            if (e.target !== inputRef.current) e.preventDefault();
            inputRef.current?.focus();
            if (openOnFocus && !disabled) {
              setActive(
                Math.max(
                  0,
                  shown.findIndex((o) => o.value === value),
                ),
              );
              setOpen(true);
            }
          }}
        >
          {selected?.icon && !typing ? (
            <span className="vel-input__adorn">{selected.icon}</span>
          ) : null}
          <input
            ref={inputRef}
            id={field?.id}
            className="vel-input__control"
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeOption ? optionId(active) : undefined}
            aria-invalid={isInvalid || undefined}
            aria-describedby={field?.describedBy}
            aria-label={aria['aria-label']}
            autoComplete="off"
            spellCheck={false}
            placeholder={placeholder}
            disabled={disabled}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setTyping(true);
              setActive(0);
              setOpen(true);
              onInputChange?.(e.target.value);
            }}
            onKeyDown={onKeyDown}
            onFocus={() => {
              if (openOnFocus && !disabled) setOpen(true);
            }}
            onBlur={() => {
              setOpen(false);
              setTyping(false);
              setText(selected?.label ?? '');
            }}
          />
          {loading ? (
            <span className="vel-input__adorn">
              <Spinner size={12} label="Loading" />
            </span>
          ) : null}
          {clearable && value !== null && !disabled ? (
            <button
              type="button"
              className="vel-combobox__clear"
              aria-label="Clear selection"
              tabIndex={-1}
              onClick={() => commit(null)}
            >
              <X size={12} aria-hidden />
            </button>
          ) : null}
          <span className="vel-input__adorn">
            <ChevronDown size={14} aria-hidden />
          </span>
        </div>
      </RP.Anchor>
      <RP.Portal>
        <RP.Content
          className="vel-float vel-combobox__popup"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (e.target instanceof Node && inputRef.current?.parentElement?.contains(e.target)) {
              e.preventDefault();
            }
          }}
        >
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={aria['aria-label'] ?? 'Options'}
          >
            {shown.length === 0 ? (
              <div className="vel-combobox__empty">{loading ? 'Searching' : emptyText}</div>
            ) : (
              shown.map((o, i) => (
                <div
                  key={o.value}
                  id={optionId(i)}
                  data-index={i}
                  role="option"
                  aria-selected={o.value === value}
                  aria-disabled={o.disabled || undefined}
                  data-disabled={o.disabled ? '' : undefined}
                  data-active={i === active ? '' : undefined}
                  className="vel-option"
                  onPointerDown={(e) => e.preventDefault()}
                  onPointerMove={() => setActive(i)}
                  onClick={() => !o.disabled && commit(o)}
                >
                  {o.icon ? <span className="vel-option__icon">{o.icon}</span> : null}
                  <span className="vel-combobox__text">
                    {o.badge ? (
                      <span className="vel-combobox__labelrow">
                        <span>{o.label}</span>
                        {o.badge}
                      </span>
                    ) : (
                      <span>{o.label}</span>
                    )}
                    {o.description ? (
                      <span className="vel-combobox__desc">{o.description}</span>
                    ) : null}
                  </span>
                  {o.value === value ? (
                    <span className="vel-option__check">
                      <Check size={14} aria-hidden />
                    </span>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </RP.Content>
      </RP.Portal>
    </RP.Root>
  );
}
